'use client';

import { useTranslations, useLocale } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { getLocalizedRoute, getCurrentPage, Locale } from '@/config/routes';
import { getLocaleFlagUrl, getLocaleDisplayName } from '@/lib/locale';

const Navigation = () => {
  const t = useTranslations('nav');
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const languages = [
    { code: 'bs' as Locale, name: getLocaleDisplayName('bs'), flag: getLocaleFlagUrl('bs') },
    { code: 'en' as Locale, name: getLocaleDisplayName('en'), flag: getLocaleFlagUrl('en') },
    { code: 'tr' as Locale, name: getLocaleDisplayName('tr'), flag: getLocaleFlagUrl('tr') }
  ];

  const currentLanguage = languages.find(lang => lang.code === locale);

  const switchLanguage = (newLocale: Locale) => {
    const currentPage = getCurrentPage(pathname);
    const newPath = currentPage
      ? getLocalizedRoute(currentPage, newLocale)
      : getLocalizedRoute('home', newLocale);
    router.push(newPath);
    setIsLangOpen(false);
  };

  const navLinks = [
    { href: getLocalizedRoute('about', locale), label: t('about') },
    { href: getLocalizedRoute('services', locale), label: t('services') },
    { href: getLocalizedRoute('blog', locale), label: t('blog') },
    { href: getLocalizedRoute('contact', locale), label: t('contact') },
  ];

  return (
    <nav className="fixed top-4 left-4 right-4 z-50 animate-slide-nav">
      <div className={`max-w-7xl mx-auto px-6 lg:px-8 rounded-2xl transition-all duration-300 border ${
        isScrolled || isMenuOpen
          ? 'bg-white/30 backdrop-blur-md shadow-lg border-white/10'
          : 'bg-transparent border-transparent'
      }`}>
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href={getLocalizedRoute('home', locale)} className="flex items-center">
            <Image
              src="/images/logo.svg"
              alt="BubbleZone"
              width={150}
              height={34}
              className="h-8 w-auto"
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-foreground hover:text-primary transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Switcher + Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center space-x-2 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Switch language"
              >
                <Image
                  src={currentLanguage?.flag || getLocaleFlagUrl('en')}
                  alt={currentLanguage?.name || 'Language'}
                  width={24}
                  height={18}
                  className="rounded-sm"
                  unoptimized
                />
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isLangOpen && (
                <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg py-2 z-50 border overflow-hidden min-w-max">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 transition-colors whitespace-nowrap ${
                        locale === lang.code ? 'bg-gray-50' : ''
                      }`}
                    >
                      <Image
                        src={lang.flag}
                        alt={lang.name}
                        width={24}
                        height={18}
                        className="rounded-sm flex-shrink-0"
                        unoptimized
                      />
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div id="mobile-menu" className="md:hidden py-4 border-t border-gray-200/50">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="block py-3 text-foreground hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
